import { useTableActionContext } from '@aibees/elephant/context/table-action/context';
import { useLabelGroupingStatus } from '@aibees/elephant/hooks/labels/use-label-grouping-status';
import { useGetCreditsAmountTypeOnActionPopup } from '@aibees/elephant/hooks/use-get-credits-amount-type-on-action-popup';
import { LabelType } from '@aibees/elephant/types/label';
import {
  ACCORDION_IDS,
  DIALOG_IDS,
  getAccordionIdWithType,
  useTableAccordionStore,
} from '@aibees/shared/store/table-accordion-store';
import { useWorkspace } from '@aibees/shared/workspace';
import { useCallback, useEffect, useMemo } from 'react';

export function useAssignOwnerDialog() {
  // Context hooks
  const {
    handleAssignOwner,
    ownerDialogState,
    ids,
    handleClose,
    previousState,
    setPreviousState,
  } = useTableActionContext();
  const grouping = useLabelGroupingStatus();
  // Credit types
  const {
    aiCompany,
    aiPeople,
    aiUnsavedPeople,
    basicCompany,
    basicPeople,
    basicUnsavedPeople,
  } = useGetCreditsAmountTypeOnActionPopup();

  // Other hooks
  const workspace = useWorkspace();

  // Memoized values
  const showAiLookalike = useMemo(
    () =>
      aiCompany ||
      aiPeople ||
      aiUnsavedPeople ||
      basicPeople ||
      basicUnsavedPeople,
    [aiCompany, aiPeople, aiUnsavedPeople, basicPeople, basicUnsavedPeople]
  );

  // Use table accordion store directly
  const {
    getDialogExpanded,
    setDialogExpanded,
    setInitialExpanded,
    clearDialog,
  } = useTableAccordionStore();

  const dialogId = DIALOG_IDS.ASSIGN_OWNER_DIALOG;
  const expandedAccordion = getDialogExpanded(dialogId);

  // Determine initial expanded accordion based on previousState
  const getInitialAccordionId = useCallback(() => {
    if (previousState === LabelType.LABEL) {
      return getAccordionIdWithType('LABEL');
    }
    if (previousState === LabelType.LIST) {
      return getAccordionIdWithType('LIST');
    }
    return ACCORDION_IDS.ASSIGN_OWNER;
  }, [previousState]);

  // Set initial expanded accordion when dialog opens
  useEffect(() => {
    if (ownerDialogState.value) {
      setInitialExpanded(dialogId, getInitialAccordionId());
    }
  }, [
    ownerDialogState.value,
    dialogId,
    setInitialExpanded,
    getInitialAccordionId,
  ]);

  // Clear dialog state when dialog closes
  useEffect(() => {
    if (!ownerDialogState.value) {
      clearDialog(dialogId);
    }
  }, [ownerDialogState.value, clearDialog, dialogId]);

  // Handlers
  const setExpandedAccordion = useCallback(
    (accordionId: string | null) => {
      setDialogExpanded(dialogId, accordionId);
    },
    [dialogId, setDialogExpanded]
  );

  const handleDialogClose = useCallback(() => {
    handleClose('owner');
    setPreviousState('');
  }, [handleClose, setPreviousState]);

  return {
    // State
    ownerDialogState,
    ids,
    expandedAccordion,
    showAiLookalike,
    workspace,
    grouping,
    setPreviousState,
    // Handlers
    handleAssignOwner,
    handleClose,
    handleDialogClose,
    setExpandedAccordion,

    // Computed values
    dialogId,
    hasIds: ids.length > 0,
  };
}
